/**
 * Inter-shop settlements: stock that ended up at the wrong shop, usually because a customer
 * returned an item to a branch that did not sell it.
 *
 * There are exactly two honest ways to close one, and the UI must offer both rather than picking:
 *
 * - **Accept it.** The shop that has the stock keeps it and the imbalance is written off between
 *   them. Correct when the item is worth less than the trip.
 * - **Settle it by transfer.** The stock physically goes back, referenced to a real transfer, and
 *   the settlement closes when that transfer does.
 *
 * A settlement belongs to *two* shops, so it has no per-shop cache key — the same row appears in
 * both shops' queues, and a per-shop tag would need invalidating twice.
 */

import { api } from '../api.js';
import { settlementListTag, stockListTag, transferListTag } from '../tags.js';
import type { Paged, ShopId } from '../../types/api.js';
import type { Settlement, SettlementId, TransferId } from '../../types/stockIntegrity.js';

export type ListSettlementsParams = {
  readonly shopId?: ShopId | undefined;
  readonly onlyOpen?: boolean | undefined;
  readonly page?: number | undefined;
  readonly pageSize?: number | undefined;
};

export const settlementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    listSettlements: build.query<Paged<Settlement>, ListSettlementsParams>({
      query: (params) => ({ url: '/settlements', params: { page: 1, pageSize: 25, ...params } }),
      providesTags: [settlementListTag()],
    }),

    /** The report behind the queue: which shops keep absorbing other shops' returns, and for how much. */
    listCrossShopReturns: build.query<
      Settlement[],
      { shopId?: ShopId | undefined; from?: string | undefined; to?: string | undefined }
    >({
      query: (params) => ({ url: '/settlements/cross-shop-returns', params }),
      providesTags: [settlementListTag()],
    }),

    /** The shop holding the stock keeps it. No stock moves; the imbalance is simply closed. */
    acceptSettlement: build.mutation<void, { id: SettlementId; note?: string | undefined }>({
      query: ({ id, note }) => ({
        url: `/settlements/${id}/acceptance`,
        method: 'POST',
        body: { note },
      }),
      invalidatesTags: [settlementListTag()],
    }),

    /**
     * Closed against a real transfer, so the stock actually going home is the thing that settles
     * it. The transfer list is invalidated too: that transfer now has a settlement pointing at it.
     */
    settleByTransfer: build.mutation<void, { id: SettlementId; transferId: TransferId }>({
      query: ({ id, transferId }) => ({
        url: `/settlements/${id}/transfer-settlement`,
        method: 'POST',
        body: { transferId },
      }),
      invalidatesTags: [settlementListTag(), transferListTag(), stockListTag()],
    }),
  }),
});

export const {
  useListSettlementsQuery,
  useListCrossShopReturnsQuery,
  useAcceptSettlementMutation,
  useSettleByTransferMutation,
} = settlementsApi;
